import type {
	AccessEvent as DbAccessEvent,
	Account as DbAccount,
	Organization as DbOrganization,
	Role as DbRole,
	RoleGrant as DbRoleGrant,
} from '@rentflowdev/database'

import type { CurrencyCode } from './common'

/** Mirrors `Account` aggregate returned by `account-service` over gRPC. */
export const ACCOUNT_ROLES = ['USER', 'ADMIN'] as const
export type AccountRole = typeof ACCOUNT_ROLES[number]

export const ACCOUNT_TYPES = ['INDIVIDUAL', 'ENTREPRENEUR', 'COMPANY'] as const
export type AccountType = typeof ACCOUNT_TYPES[number]

/**
 * Налоговый режим арендодателя. Вместе с типом объекта определяет ставку НДС —
 * вручную она больше не вводится.
 *
 * NONE — режим ещё не указан: форма условий аренды показывает предупреждение
 * вместо расчёта.
 */
export const TAX_REGIMES = ['NONE', 'OSNO', 'USN', 'AUSN', 'PSN', 'NPD', 'INDIVIDUAL'] as const
export type TaxRegime = typeof TAX_REGIMES[number]

/** Доход за предыдущий год на УСН: до 20 млн — без НДС, 20–250 — 5%, 250–450 — 7%. */
export const USN_INCOME_BANDS = ['UP_TO_20M', 'FROM_20M_TO_250M', 'FROM_250M_TO_450M'] as const
export type UsnIncomeBand = typeof USN_INCOME_BANDS[number]

export interface Account {
	id: string
	phone: string | null
	email: string | null
	isPhoneVerified: boolean
	isEmailVerified: boolean
	role: AccountRole
	accountType?: AccountType
	telegramId: string | null
	parentAccountId?: string | null
	taxRegime?: TaxRegime
	usnIncomeBand?: UsnIncomeBand | null
	vatExemptArt145?: boolean
	vatVoluntary22?: boolean
	/** Валюта по умолчанию для новых записей; сохранённые хранят свою. */
	currency?: CurrencyCode
	createdAt?: string
	updatedAt?: string
}

export const SIGNATORY_BASIS = ['PERSONAL', 'POWER_OF_ATTORNEY', 'CHARTER', 'OTHER'] as const
export type SignatoryBasis = typeof SIGNATORY_BASIS[number]

export interface Signatory {
	id?:              string
	fullName:         string
	position?:        string
	basis:            SignatoryBasis
	proxyNumber?:     string
	proxyValidFrom?:  string
	proxyValidUntil?: string
	isSelf?:          boolean
}

export interface UpsertSignatoryBody {
	id?:              string
	fullName:         string
	position?:        string
	basis:            SignatoryBasis
	proxyNumber?:     string
	proxyValidFrom?:  string
	proxyValidUntil?: string
}

export interface CompanyProfile {
	id?: string
	accountId: string
	name?: string
	inn?: string
	kpp?: string
	ogrn?: string
	bank?: string
	bankAccount?: string
	corrAccount?: string
	bik?: string
	address?: string
	registrationAddr?: string
	passportSeries?: string
	passportNumber?: string
	passportIssuedBy?: string
	passportIssueDate?: string
	passportDepartmentCode?: string
	signatory?: string
	signatoryBasis?: SignatoryBasis
	signatories?: Signatory[]
	createdAt?: string
	updatedAt?: string
}

export interface UpdateAccountTypeBody {
	accountType: AccountType
}

export interface UpdateTaxRegimeBody {
	taxRegime: TaxRegime
	/** Обязателен для УСН, если не выбрана общая ставка 22%. */
	usnIncomeBand?: UsnIncomeBand
	vatExemptArt145?: boolean
	vatVoluntary22?: boolean
}

export interface UpdateCurrencyBody {
	currency: CurrencyCode
}

export interface InitEmailChangeBody {
	email: string
}
export interface ConfirmEmailChangeBody {
	code:  string
	email: string
}

export interface InitPhoneChangeBody {
	phone: string
}
export interface ConfirmPhoneChangeBody {
	code:  string
	phone: string
}

export interface UpdateTelegramBody {
	telegramId: string | null
}

export interface UpsertCompanyProfileBody {
	name?: string
	inn?: string
	kpp?: string
	ogrn?: string
	bank?: string
	bankAccount?: string
	corrAccount?: string
	bik?: string
	address?: string
	registrationAddr?: string
	passportSeries?: string
	passportNumber?: string
	passportIssuedBy?: string
	passportIssueDate?: string
	passportDepartmentCode?: string
	signatory?: string
	signatoryBasis?: SignatoryBasis
	signatories?: UpsertSignatoryBody[]
}

// ── Company documents (Свидетельство ИП/ООО, Выписка ЕГРЮЛ и т.п.) ──

export type CompanyDocumentKind =
	| 'REGISTRATION_CERTIFICATE'   // Свидетельство о регистрации ИП/ООО
	| 'EGRUL_EXCERPT'              // Выписка из ЕГРЮЛ
	| 'CHARTER'                    // Устав
	| 'OTHER'

export interface CompanyDocument {
	id:         string
	accountId:  string
	kind:       CompanyDocumentKind
	fileUrl:    string
	fileName:   string
	fileSize?:  number
	mimeType?:  string
	uploadedAt: string
}

export interface ListCompanyDocumentsResponse {
	items: CompanyDocument[]
}

/**
 * Тело запроса после загрузки файла на S3/MinIO. Загрузка идёт через presigned-URL
 * (см. `generateUploadUrl` в property.ts), а сюда фронт шлёт уже финальный fileUrl.
 */
export interface UploadCompanyDocumentBody {
	kind:      CompanyDocumentKind
	fileUrl:   string
	fileName:  string
	fileSize?: number
	mimeType?: string
}

/**
 * A role's key (ADR 0003). Roles are data the platform's super admin
 * manages; an organization lists the ones it may give in `Organization.roles`.
 */
export type SubAccountRole = NonNullable<DbAccount['subRole']>

type AssertNever<T extends never> = T

/**
 * The line of business an organization works in, in the order the settings
 * offer them. It decides which team roles the organization may give.
 */
export const ORGANIZATION_SEGMENTS = ['PRIVATE_LANDLORD', 'MANAGEMENT_COMPANY', 'AGENCY'] as const satisfies readonly DbOrganization['segment'][]
export type OrganizationSegment = typeof ORGANIZATION_SEGMENTS[number]
export type _EveryOrganizationSegmentListed = AssertNever<Exclude<DbOrganization['segment'], OrganizationSegment>>

/** The caller's organization, as `GET /account/organization` returns it. */
/** A role as members see it. */
export type RoleSummary = Pick<DbRole, 'key' | 'name'> & Partial<Pick<DbRole, 'description'>>

export interface Organization {
	id:          string
	segment:     OrganizationSegment
	/** Team roles the organization may give its members, in display order. */
	roles:       RoleSummary[]
	/** @deprecated The keys of `roles`; read `roles`. */
	memberRoles: SubAccountRole[]
	/** Whether the caller may change the organization's settings. */
	canUpdate:   boolean
}

export interface UpdateOrganizationSegmentBody {
	segment: OrganizationSegment
}

export const SUB_ACCOUNT_STATUSES = ['ACTIVE', 'INVITED', 'BLOCKED'] as const
export type SubAccountStatus = typeof SUB_ACCOUNT_STATUSES[number]

export interface SubAccountProperty {
	propertyId: string
	title?:     string
	address?:   string
	imageUrl?:  string
}

/** Sub-account = a child account (agent/manager) of the current account. */
export interface SubAccount {
	id:         string
	fullName:   string
	email?:     string | null
	phone?:     string | null
	role:       SubAccountRole
	/** The role's name, as the super admin set it. */
	roleName?:  string | null
	status:     SubAccountStatus
	properties: SubAccountProperty[]
	invitedAt?: string
	createdAt?: string
	updatedAt?: string
}

export interface ListSubAccountsParams {
	status?:     SubAccountStatus
	role?:       SubAccountRole
	propertyId?: string
	search?:     string
}

export interface InviteSubAccountBody {
	fullName:     string
	phone?:       string
	email?:       string
	role:         SubAccountRole
	propertyIds?: string[]
}

export interface UpdateSubAccountBody {
	role?:   SubAccountRole
	status?: SubAccountStatus
}

export interface AssignSubAccountPropertyBody {
	propertyId: string
}

// ── Account requisites (for document wizard) ──────────────────
//
// The document wizard's landlord step needs to pick a bank account and a
// signatory from the current account's saved requisites. The DTOs below are
// the contract the gateway must expose; the frontend reads them via
// `useListAccountBankAccountsQuery()` / `useListAccountSignatoriesQuery()`.

export interface AccountBankAccount {
	id: string
	accountId: string
	bankName: string
	bankAccount: string
	corrAccount?: string
	bik?: string
	isPrimary?: boolean
	createdAt?: string
	updatedAt?: string
}

export interface AccountSignatory {
	id: string
	accountId: string
	fullName: string
	position?: string
	basis?: SignatoryBasis
	isPrimary?: boolean
	createdAt?: string
	updatedAt?: string
}

export interface ListAccountBankAccountsResponse {
	items: AccountBankAccount[]
}

export interface ListAccountSignatoriesResponse {
	items: AccountSignatory[]
}

// ── Roles: the platform's super admin (ADR 0003) ─────────────────────

/** One grant: an action on a resource, over the rows the scopes name. */
export type RoleGrant = Pick<DbRoleGrant, 'resource' | 'action' | 'scopes'>

/** A role with what it may do, as the super admin manages it. */
export type RoleDefinition =
	Pick<DbRole, 'key' | 'name' | 'description' | 'system' | 'segments' | 'position'> & {
		grants:      RoleGrant[]
		/** Members holding the role; a role in use cannot be removed. */
		memberCount: number
	}

/** What grants may contain for one resource. */
export interface RoleVocabularyEntry {
	resource: string
	actions:  string[]
	scopes:   string[]
}

export type SaveRoleBody = Pick<DbRole, 'name' | 'segments' | 'position'> & {
	description?: string
	grants:       RoleGrant[]
}

export interface CreateRoleBody extends SaveRoleBody {
	key: SubAccountRole
}

/**
 * What the caller may do (`GET /account/access`): their rules from the role
 * catalog, packed — the same the gateway's route guard checks — and their
 * role's grants, which tell which sections open. Read them with
 * `abilityFromPacked` and `grantsSection` from `@rentflowdev/access`; typed
 * loosely here so apps that never read them (oauth-app) need not build that
 * package.
 */
export interface AccessRules {
	rules: unknown[]
	/** Absent from a gateway older than this field. */
	grants?: Record<string, unknown>
}

/**
 * One entry of the organization's access journal (`GET /account/access-events`):
 * a refusal, a role change or a row handed to someone.
 */
export type AccessEventEntry = Pick<
	DbAccessEvent,
	'id' | 'kind' | 'actorId' | 'resource' | 'action' | 'targetId'
> & {
	at: string
	/** The actor as people know them: name, else email, else phone. */
	actorName: string | null
	/** The route refused, the roles before and after, whom a row went to. */
	details: Record<string, unknown>
}

